import React, { useState } from 'react';
import toast from 'react-hot-toast';
import { authApi } from '../api/auth';
import { Mail, Lock, User, ChevronRight, Leaf } from 'lucide-react';

export default function Login({ onLogin }) {
  const [isRegister, setIsRegister] = useState(false);
  const [formData, setFormData] = useState({
    email: '',
    password: '',
    full_name: '',
    role: 'owner'
  });
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      if (isRegister) {
        await authApi.register(formData);
        toast.success('Registrasi berhasil! Silakan login.');
        setIsRegister(false);
        setFormData({ email: '', password: '', full_name: '', role: 'owner' });
      } else {
        const response = await authApi.login({
          email: formData.email,
          password: formData.password
        });
        toast.success('Login berhasil!');
        onLogin(response.user);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Terjadi kesalahan');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex" data-testid="login-page">
      {/* Left Side - Palm Oil Plantation */}
      <div className="hidden lg:flex lg:w-[55%] relative overflow-hidden">
        <div
          className="absolute inset-0 bg-cover bg-center"
          style={{ backgroundImage: 'url(https://images.unsplash.com/photo-1593924689241-1b78c38f0071?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NTYxODd8MHwxfHNlYXJjaHwzfHxwYWxtJTIwb2lsJTIwcGxhbnRhdGlvbiUyMGFlcmlhbCUyMHZpZXd8ZW58MHx8fHwxNzczMjg0NTk4fDA&ixlib=rb-4.1.0&q=85)' }}
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#1A4D2E]/90 via-[#1A4D2E]/40 to-transparent" />
        <div className="relative z-10 flex flex-col justify-end p-12 pb-16">
          <div className="mb-6">
            <img
              src="/kansha-logo-gold-only.png"
              alt="Kansha Group"
              className="w-80 h-auto mb-8 drop-shadow-2xl"
            />
            <p className="text-lg text-white/90 max-w-md leading-relaxed mt-2 italic" style={{ fontFamily: 'Georgia, serif' }}>
              "Mengakar dalam Syukur, Mekar dalam Inovasi"
            </p>
            <div className="mt-4 flex items-center gap-3">
              <div className="h-px w-10 bg-amber-300/50" />
              <p className="text-xs text-amber-300 tracking-[0.3em] uppercase font-medium">Fide et Gratia</p>
              <div className="h-px w-10 bg-amber-300/50" />
            </div>
          </div>
          <div className="flex gap-8 mt-4">
            <div className="text-center">
              <div className="text-2xl font-bold text-amber-300" style={{ fontFamily: 'Chivo, sans-serif' }}>24/7</div>
              <div className="text-xs text-white/60 mt-1">Akses Penuh</div>
            </div>
            <div className="w-px bg-white/20" />
            <div className="text-center">
              <div className="text-2xl font-bold text-amber-300" style={{ fontFamily: 'Chivo, sans-serif' }}>Real-time</div>
              <div className="text-xs text-white/60 mt-1">Laporan Keuangan</div>
            </div>
            <div className="w-px bg-white/20" />
            <div className="text-center">
              <div className="text-2xl font-bold text-amber-300" style={{ fontFamily: 'Chivo, sans-serif' }}>Aman</div>
              <div className="text-xs text-white/60 mt-1">Data Terenkripsi</div>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Login Form */}
      <div className="w-full lg:w-[45%] flex items-center justify-center p-8 bg-[#FDFBF7]">
        <div className="w-full max-w-md">
          {/* Logo (centered, all viewports) */}
          <div className="mb-8 text-center">
            <img
              src="/kansha-logo-gold-only.png"
              alt="Kansha Group"
              className="mx-auto w-56 lg:w-64 h-auto"
            />
          </div>

          <div className="mb-8">
            <h2 className="text-3xl font-bold text-[#0F172A] mb-2" style={{ fontFamily: 'Chivo, sans-serif' }}>
              {isRegister ? 'Daftar Akun' : 'Selamat Datang'}
            </h2>
            <p className="text-stone-500">{isRegister ? 'Buat akun baru untuk memulai' : 'Masuk ke dashboard Anda'}</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4" data-testid="login-form">
            {isRegister && (
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1.5">Nama Lengkap</label>
                <div className="relative">
                  <User size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400" />
                  <input type="text" value={formData.full_name} onChange={(e) => setFormData({ ...formData, full_name: e.target.value })} className="w-full pl-11 pr-4 py-3 bg-white border border-stone-200 rounded-xl focus:border-[#D4A373] focus:ring-2 focus:ring-[#D4A373]/20 transition-all" required data-testid="full-name-input" placeholder="Nama lengkap Anda" />
                </div>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">Email</label>
              <div className="relative">
                <Mail size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400" />
                <input type="email" value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className="w-full pl-11 pr-4 py-3 bg-white border border-stone-200 rounded-xl focus:border-[#D4A373] focus:ring-2 focus:ring-[#D4A373]/20 transition-all" required data-testid="email-input" placeholder="email@perusahaan.com" />
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-stone-700 mb-1.5">Password</label>
              <div className="relative">
                <Lock size={18} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-stone-400" />
                <input type="password" value={formData.password} onChange={(e) => setFormData({ ...formData, password: e.target.value })} className="w-full pl-11 pr-4 py-3 bg-white border border-stone-200 rounded-xl focus:border-[#D4A373] focus:ring-2 focus:ring-[#D4A373]/20 transition-all" required data-testid="password-input" placeholder="Kata sandi" />
              </div>
            </div>

            {isRegister && (
              <div>
                <label className="block text-sm font-medium text-stone-700 mb-1.5">Role</label>
                <select value={formData.role} onChange={(e) => setFormData({ ...formData, role: e.target.value })} className="w-full px-4 py-3 bg-white border border-stone-200 rounded-xl focus:border-[#D4A373] focus:ring-2 focus:ring-[#D4A373]/20" data-testid="role-select">
                  <option value="owner">Owner</option>
                  <option value="admin">Admin</option>
                  <option value="viewer">Viewer</option>
                </select>
              </div>
            )}

            <button type="submit" disabled={loading} className="w-full bg-[#1A4D2E] text-white py-3.5 rounded-xl font-medium hover:bg-[#143d24] transition-all duration-200 shadow-lg shadow-[#1A4D2E]/20 hover:shadow-xl hover:shadow-[#1A4D2E]/30 disabled:opacity-50 flex items-center justify-center gap-2 group" data-testid="submit-button">
              {loading ? 'Memproses...' : (isRegister ? 'Daftar' : 'Masuk')}
              {!loading && <ChevronRight size={18} className="group-hover:translate-x-1 transition-transform" />}
            </button>
          </form>

          <div className="mt-6 text-center">
            <button onClick={() => setIsRegister(!isRegister)} className="text-stone-500 hover:text-[#1A4D2E] text-sm transition-colors" data-testid="toggle-mode-button">
              {isRegister ? 'Sudah punya akun? Masuk di sini' : 'Belum punya akun? Daftar di sini'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
